<script setup lang="ts">
import { Tippy } from 'vue-tippy'
import { AxiosError } from 'axios'
import type { Outlines } from '@components/NovaIcon/NovaIcon.types'
import type { DropdownItemType } from '@components/NovaDropdownItem/NovaDropdownItem.types'
import {
  ApplyCreatorAttachContentType,
  type ApplyCreatorConditionType,
  type ApplyCreatorPayload,
} from '@store'

interface Activity {
  count: number
  value: string
  placeholder: string
  errorMsg: string
  onChangeValue: () => void
  validator: (showErrorMsg?: boolean) => boolean
}

interface RepresentativeValue {
  url: string
  file?: File
}

interface NewRepresentativeValidator {
  pass: boolean
  message: string
  value: RepresentativeValue
  type: ApplyCreatorAttachContentType
}

interface NewRepresentative {
  type: ApplyCreatorAttachContentType
  value: RepresentativeValue
  placeholder: string
  isShowAddField: boolean
  deleteLabel: string
  addLabel: string
  addLoading: boolean
  validator: () => NewRepresentativeValidator
  onRepresentativeFile: () => Promise<void>
  onRepresentativeAdd: () => void
  onClickContentType: (type: ApplyCreatorAttachContentType) => void
  clearNewRepresentativeModel: (type: ApplyCreatorAttachContentType) => void
}

interface Representative {
  count: number
  maxCount: number
  value: {
    value: RepresentativeValue
    type: ApplyCreatorAttachContentType
  }[]
  errorMsg: string
  validator: (showErrorMsg?: boolean) => boolean
  descriptions: string[]
}

interface CreatorCategory {
  value: number
  placeholder: string
  categories: DropdownItemType[]
  errorMsg: string
  onChangeValue: (value: number) => void
  validator: (showErrorMsg?: boolean) => boolean
}

definePageMeta({
  layout: 'one-column',
})

useSetSEO()

const { gtEvent } = useGoogleTag()
const { t, locale, messages } = useI18n()
const config = useRuntimeConfig()
const route = useRoute()
const layoutStore = useLayoutStore()
const userStore = useUserStore()
const creatorStore = useCreatorStore()
const mypageStore = useMyPageStore()
const { applyCreatorAction, callDialog } = useApplyCreatorProcess()

// 모델: 크리에이터 전환 신청 요건(최초 랜딩시 초기화)
const applyType = ref<ApplyCreatorConditionType>('CUAR')

// 모델: 활동 계획
const activity = reactive<Activity>({
  count: 300,
  value: '',
  placeholder: t(
    'applyCreator.conditionsContent.sections.activity.textField.placeholder'
  ),
  errorMsg: '',
  onChangeValue: () => {
    const isValid = activity.validator(false)
    if (isValid) {
      activity.errorMsg = ''
    }
  },
  validator: (showErrorMsg = true): boolean => {
    const pass = !(activity.value.length < activity.count)
    if (showErrorMsg) {
      activity.errorMsg = pass
        ? ''
        : t(
            'applyCreator.conditionsContent.sections.activity.textField.error',
            { count: activity.count }
          )
    }
    return pass
  },
})

// 모델: 신청 정보 / NEW 대표 콘텐츠
const newRepresentativeRef = ref()
const newRepresentative = reactive<NewRepresentative>({
  type: ApplyCreatorAttachContentType.LINK,
  value: { url: '' },
  placeholder: t(
    `applyCreator.conditionsContent.sections.info.contentsPlaceholder.${ApplyCreatorAttachContentType.LINK}`
  ),
  isShowAddField: false,
  deleteLabel: t('delete'),
  addLabel: t('reg'),
  addLoading: false,
  validator: (): NewRepresentativeValidator => {
    const value = newRepresentative.value
    const type = newRepresentative.type

    const linkValidator = () => {
      // 링크 중복 검사
      const isDuplicate =
        representative.value
          .filter((item) => item.type === ApplyCreatorAttachContentType.LINK)
          .findIndex((item) => item.value.url === value.url) !== -1
      if (isDuplicate) {
        return {
          pass: false,
          message: t(
            'applyCreator.conditionsContent.sections.info.contentsDuplicate'
          ),
          value,
          type,
        }
      }

      // 링크 유효성 검사
      const isPass = value.url.match(/^http(s)?:\/\/\S+[.]\S+/g) !== null
      if (!isPass) {
        return {
          pass: false,
          message: t(
            'applyCreator.conditionsContent.sections.info.contentsLinkError'
          ),
          value,
          type,
        }
      }

      return {
        pass: true,
        message: '',
        value,
        type,
      }
    }
    const fileValidator = () => {
      // 파일 중복 검사
      const isDuplicate =
        representative.value
          .filter((item) => item.type === ApplyCreatorAttachContentType.FILE)
          .findIndex((item) => item.value.url === value.url) !== -1
      if (isDuplicate) {
        return {
          pass: false,
          message: t(
            'applyCreator.conditionsContent.sections.info.contentsDuplicate'
          ),
          value,
          type,
        }
      }

      // 파일 유효성 검사
      const isPass = !!value.url.length && !!value.file
      if (!isPass) {
        return {
          pass: false,
          message: t(
            'applyCreator.conditionsContent.sections.info.contentsFileEmpty'
          ),
          value,
          type,
        }
      }

      return {
        pass: true,
        message: '',
        value,
        type,
      }
    }

    if (
      representative.value.filter(
        (item) => item.type === type && item.value === value
      ).length
    ) {
      return {
        pass: false,
        message: t(
          'applyCreator.conditionsContent.sections.info.contentsDuplicate'
        ),
        value,
        type,
      }
    }

    switch (newRepresentative.type) {
      case ApplyCreatorAttachContentType.LINK:
        return linkValidator()
      case ApplyCreatorAttachContentType.FILE:
        return fileValidator()
      default:
        return {
          pass: false,
          message: '',
          value,
          type,
        }
    }
  },
  onRepresentativeFile: async () => {
    if (newRepresentative.type !== ApplyCreatorAttachContentType.FILE) return

    const formDataName = 'file'
    const { files } = await useGetFiles({
      accept:
        'image/*,video/*,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf',
      multiple: false,
      formDataName,
    })
    const file = files ? files[0] : null

    if (file instanceof File) {
      newRepresentative.value = { url: file.name, file }
      newRepresentative.onRepresentativeAdd()
    } else {
      useToast(
        t('applyCreator.conditionsContent.sections.info.contentsFileEmpty')
      )
    }
  },
  onRepresentativeAdd: () => {
    try {
      newRepresentative.addLoading = true
      const valid = newRepresentative.validator()
      if (!valid.pass) {
        useToast(valid.message)
        return
      }
      representative.value.push({
        value: newRepresentative.value,
        type: valid.type,
      })
      newRepresentative.clearNewRepresentativeModel(valid.type)
      const isValid = representative.validator(false)
      if (isValid) {
        representative.errorMsg = ''
      }
      newRepresentative.isShowAddField = false
    } finally {
      newRepresentative.addLoading = false
    }
  },
  onClickContentType: (type: ApplyCreatorAttachContentType) => {
    newRepresentative.clearNewRepresentativeModel(type)
    newRepresentativeRef.value?.hide()

    if (type === ApplyCreatorAttachContentType.FILE) {
      newRepresentative.isShowAddField = false
      newRepresentative.onRepresentativeFile()
      return
    }

    if (type === ApplyCreatorAttachContentType.LINK) {
      newRepresentative.isShowAddField = true
    }
  },
  clearNewRepresentativeModel: (type: ApplyCreatorAttachContentType) => {
    newRepresentative.type = type
    newRepresentative.placeholder = t(
      `applyCreator.conditionsContent.sections.info.contentsPlaceholder.${type}`
    )
    newRepresentative.value = { url: '' }
  },
})

// 모델: 신청 정보 / 대표 콘텐츠
const representative = reactive<Representative>({
  count: 3,
  maxCount: 5,
  value: [],
  errorMsg: '',
  validator: (showErrorMsg = true): boolean => {
    if (applyType.value === 'CUFW') return true

    const pass = !(representative.value.length < representative.count)
    if (showErrorMsg) {
      representative.errorMsg = pass
        ? ''
        : t('applyCreator.conditionsContent.sections.info.error', {
            count: representative.count,
          })
    }
    return pass
  },
  descriptions: Object.keys(
    (messages.value[locale.value].applyCreator as any).conditionsContent
      .sections.info.descriptions
  ).map((key) =>
    t(`applyCreator.conditionsContent.sections.info.descriptions.${key}`)
  ),
})

// 모델: 신청 정보 / SNS 주소 입력
const sns = reactive({
  value: '',
  placeholder: t('applyCreator.conditionsContent.sections.info.snsPlaceholder'),
  errorMsg: '',
  onChangeValue: () => {
    const isValid = sns.validator(false)

    if (isValid) {
      sns.errorMsg = ''
    }
  },
  validator: (showErrorMsg = true): boolean => {
    const isSnsUrl = sns.value.match(/^http(s)?:\/\/\S+[.]\S+/g) !== null

    if (applyType.value === 'CUAR') {
      const isPass = sns.value.length ? isSnsUrl : true
      if (showErrorMsg) {
        sns.errorMsg = isPass
          ? ''
          : t('applyCreator.conditionsContent.sections.info.snsErrMsg')
      }
      return isPass
    }

    if (applyType.value === 'CUFW') {
      if (showErrorMsg) {
        sns.errorMsg = !isSnsUrl
          ? t('applyCreator.conditionsContent.sections.info.snsErrMsg')
          : ''
      }
      return isSnsUrl
    }

    sns.errorMsg = ''
    return true
  },
})

// 모델: 신청 정보 / SNS 주소 본인 계정 확인
const verifiedSnsAccount = reactive({
  value: false,
  isError: false,
  onChangeValue: () => {
    const updateValue = !verifiedSnsAccount.value
    verifiedSnsAccount.value = updateValue

    if (updateValue) {
      verifiedSnsAccount.isError = false
    }
  },
  validator: (showIsError = true) => {
    const isValid = !(applyType.value === 'CUFW' && !verifiedSnsAccount.value)
    if (showIsError) {
      verifiedSnsAccount.isError = !isValid
    }
    return !(applyType.value === 'CUFW' && !verifiedSnsAccount.value)
  },
})

// 모델: 크리에이터 카테고리
const creatorCategory = reactive<CreatorCategory>({
  value: 0,
  placeholder: t(
    'applyCreator.conditionsContent.sections.creatorCategory.placeholder'
  ),
  categories: [],
  errorMsg: '',
  onChangeValue: (value: number) => {
    creatorCategory.value = value
    const isValid = creatorCategory.validator(false)
    if (isValid) {
      creatorCategory.errorMsg = ''
    }
  },
  validator: (showErrorMsg = true): boolean => {
    const pass = creatorCategory.value !== 0
    if (showErrorMsg) {
      creatorCategory.errorMsg = pass
        ? ''
        : t('applyCreator.conditionsContent.sections.creatorCategory.error')
    }
    return pass
  },
})

// 모델: 크리에이터 홈 주소
const creatorHomeLink = reactive({
  isRegistered: userStore.user?.resistReferralCodeAt === 'Y',
  minValue: 2,
  prefix: '@',
  homeUrl: `${config.public.APP_URL}/creator/@`,
  loading: false,
  isCheckDuplicate: userStore.user?.resistReferralCodeAt === 'Y',
  value: userStore.user?.referralCode || '',
  errorMsg: '',
  checkDuplicate: async (): Promise<boolean> => {
    // 1. https url 에서 사용하는 특수문자가 포함되어 있는지 여부 검사
    const isUnavailableNickname = useCheckUrlSpecialCharacter(
      creatorHomeLink.value
    )
    if (isUnavailableNickname) {
      useToast(
        t(
          'applyCreator.conditionsContent.sections.creatorHomeLink.errors.unavailableCreatorHomeLink'
        )
      )
      return false
    }

    // 2. 최소 글자수 검사
    if (creatorHomeLink.value.length < 2) {
      creatorHomeLink.errorMsg = t(
        'applyCreator.conditionsContent.sections.creatorHomeLink.errors.failLength',
        { min: creatorHomeLink.minValue }
      )
      return false
    }

    // 3. 영문 + 숫자 외 한글 혹은 특수문자 검사
    if (!/^[a-zA-Z0-9]*$/.test(creatorHomeLink.value)) {
      creatorHomeLink.errorMsg = t(
        'applyCreator.conditionsContent.sections.creatorHomeLink.errors.failCharacter'
      )
      return false
    }

    // 4. 기존 크리에이터 링크를 변경한 이력이 없고 사용자 닉네임과 같으면 등록 가능한 것으로 판단
    if (
      userStore.user?.resistReferralCodeAt === 'N' &&
      userStore.user?.userNcnm === creatorHomeLink.value
    ) {
      creatorHomeLink.errorMsg = ''
      creatorHomeLink.isCheckDuplicate = true
      return true
    }

    // 5. 중복 검사
    try {
      creatorHomeLink.loading = true
      await useSleeper()
      await creatorStore.fetchCheckDuplicateCreatorLink({
        linkNm: creatorHomeLink.value,
      })
      useToast(
        t('applyCreator.conditionsContent.sections.creatorHomeLink.success')
      )
      creatorHomeLink.errorMsg = ''
      creatorHomeLink.isCheckDuplicate = true
      return true
    } catch (err) {
      if (err instanceof AxiosError) {
        switch (err.response?.status) {
          case 5001:
            creatorHomeLink.errorMsg = t(
              'applyCreator.conditionsContent.sections.creatorHomeLink.errors.failDuplicate'
            )
            return false
          default:
            creatorHomeLink.errorMsg = t(
              'applyCreator.conditionsContent.sections.creatorHomeLink.errors.failDefault'
            )
            return false
        }
      } else {
        creatorHomeLink.errorMsg = t(
          'applyCreator.conditionsContent.sections.creatorHomeLink.errors.failDefault'
        )
        return false
      }
    } finally {
      creatorHomeLink.loading = false
    }
  },
  validator: () => {
    const pass = creatorHomeLink.isCheckDuplicate
    creatorHomeLink.errorMsg = pass
      ? ''
      : t(
          'applyCreator.conditionsContent.sections.creatorHomeLink.errors.checkDuplicate'
        )
    return pass
  },
})

// 모델: 알게된 경로
const howToKnow = reactive({
  value: '',
  placeholder: t(
    'applyCreator.conditionsContent.sections.howToKnow.placeholder'
  ),
  maxLength: 30,
})

// 모델: 심사 요청
const reqReview = reactive({
  label: t('applyCreator.conditionsContent.apply'),
  loading: false,
})

onBeforeMount(() => {
  initApplyType()
  fetchCreatorCategories()
})

onMounted(() => {
  layoutStore.updateMainBgWhite(true)
  layoutStore.updateGnbMobileHide(true)
})

onBeforeUnmount(() => {
  layoutStore.updateMainBgWhite(false)
  layoutStore.updateGnbMobileHide(false)
})

const getContentTypeIcon = (type: ApplyCreatorAttachContentType): Outlines => {
  switch (type) {
    case ApplyCreatorAttachContentType.FILE:
      return 'file'
    case ApplyCreatorAttachContentType.LINK:
    default:
      return 'link'
  }
}

const initApplyType = () => {
  const _applyType = route.query.applyType as ApplyCreatorConditionType
  const allowedValues: ApplyCreatorConditionType[] = ['CUAR', 'CUFW']

  applyType.value = allowedValues.includes(_applyType) ? _applyType : 'CUAR'
}

const handleOnClickRemoveRepresentative = (index: number) => {
  representative.value.splice(index, 1)
}

const handleOnChangeCreatorHomeLink = () => {
  creatorHomeLink.isCheckDuplicate = false
}

// 크리에이터 카테고리 조회
const fetchCreatorCategories = async () => {
  try {
    layoutStore.updateLoadingIndicatorGlobal({ show: true })
    const categires = await creatorStore.fetchCreatorCategories({
      crtrOrgnztTyCode: 'IND',
    })
    creatorCategory.categories = categires.map((item) => ({
      label: item.ctgryNm,
      value: item.crtrCtgrySn,
    }))
  } catch (err) {
  } finally {
    layoutStore.updateLoadingIndicatorGlobal({ show: false })
  }
}

// 심사 요청
const reqApplyCreator = async () => {
  const attachContents = [...representative.value]

  if (sns.value) {
    attachContents.push({
      type: ApplyCreatorAttachContentType.SNS,
      value: { url: sns.value },
    })
  }

  const attachContentsMapped = attachContents.map((item, index) => {
    const order = index + 1
    switch (item.type) {
      case ApplyCreatorAttachContentType.FILE:
        return {
          cnvrRequstRegistOrdr: order,
          crtrCnvrsRequstFile: item.value.file,
          crtrCnvrsRequstTyCode: item.type,
        }
      case ApplyCreatorAttachContentType.LINK:
      case ApplyCreatorAttachContentType.SNS:
        return {
          cnvrRequstRegistOrdr: order,
          crtrCnvrsRequstCn: item.value.url,
          crtrCnvrsRequstTyCode: item.type,
        }
      default:
        throw new Error('contents type is not match')
    }
  })

  const payload: Omit<ApplyCreatorPayload, 'attachContents'> = {
    creatorLink: creatorHomeLink.value,
    crtrActPlanCn: activity.value,
    crtrCtgrySn: creatorCategory.value,
    requstGradCndTyCode: applyType.value,
  }

  if (howToKnow.value.length) {
    payload.crtrCnvrsCognpathCn = howToKnow.value
  }

  const formData = new FormData()
  attachContentsMapped.forEach((item: any, index: number) => {
    const flattenedItem = useFlattenObject(item)
    Object.keys(flattenedItem).forEach((key) => {
      formData.append(`attachContents[${index}].${key}`, flattenedItem[key])
    })
  })

  const flattenedPayload = useFlattenObject(payload)
  Object.keys(flattenedPayload).forEach((key) => {
    formData.append(key, String(flattenedPayload[key]))
  })

  try {
    reqReview.loading = true
    await creatorStore.reqApplyCreator(formData)
    callDialog.applySuccess()
    gtEvent('userAction', {
      eventCategory: '사용자',
      eventAction: '크리에이터 신청',
      eventLabel: reqReview.label,
      eventSlot: '크리에이터 신청',
      eventI18nAddr: '',
      eventComponent: 'Button',
      creatorLink: creatorHomeLink.value,
      crtrActPlanCn: activity.value,
      crtrCtgrySn: creatorCategory.value,
      requstGradCndTyCode: applyType.value,
      crtrCnvrsCognpathCn: howToKnow.value,
    })
  } catch (err) {
    if (err instanceof AxiosError) {
      const status = err.response?.status
      const isExist =
        Object.keys(
          (messages.value[locale.value].applyCreator as any).guide.apply.message
            .errors
        ).findIndex((key) => key === String(status)) !== -1
      useToast(
        t(
          `applyCreator.guide.apply.message.errors.${
            isExist ? status : 'default'
          }`
        )
      )
    } else {
      useToast(t('applyCreator.guide.apply.message.errors.default'))
    }
  } finally {
    reqReview.loading = false
  }
}

// 심사 요청 액션
const handleOnClickReqReview = () => {
  let allPass = true
  const validators = [
    activity.validator,
    applyType.value === 'CUAR' ? representative.validator : undefined,
    sns.validator,
    applyType.value === 'CUFW' ? verifiedSnsAccount.validator : undefined,
    creatorCategory.validator,
    creatorHomeLink.validator,
  ]
  validators.forEach((validator, index) => {
    if (typeof validator !== 'undefined') {
      const valid = validator()
      console.log(index, valid)
      if (!valid) allPass = valid
    }
  })
  if (allPass) {
    const applyCreatorStatus = mypageStore.applyCreatorStatus
    const action =
      applyCreatorStatus === 'available'
        ? applyCreatorAction('available', () => {
            reqApplyCreator()
          })
        : applyCreatorAction(applyCreatorStatus)

    action()
  } else {
    gtEvent('clickEvent', {
      eventCategory: '클릭',
      eventAction: '크리에이터 신청 > 심사 요청 > 실패',
      eventLabel: '',
      eventSlot: '크리에이터 신청',
      eventI18nAddr: '',
      eventComponent: 'Button',
      activityErrorMsg: activity.errorMsg ? activity.errorMsg : '',
      creatorCategoryErrorMsg: creatorCategory.errorMsg
        ? creatorCategory.errorMsg
        : '',
      representativeErrorMsg: representative.errorMsg
        ? representative.errorMsg
        : '',
    })
  }
}
</script>

<template>
  <div class="page-apply-creator-condition-contents">
    <NovaHeaderMobile
      :show-back-action="true"
      :title="t('applyCreator.conditionsContent.pageTitle')"
    />

    <div class="inner">
      <section class="section">
        <div class="section-title-wrap">
          <h4 class="section-title">
            {{
              t('applyCreator.conditionsContent.sections.activity.title', {
                appName: config.public['APP_NAME'],
              })
            }}
            <small>
              {{
                t('applyCreator.conditionsContent.sections.activity.titleSub', {
                  count: activity.count,
                })
              }}
            </small>
          </h4>
        </div>

        <div class="form-field-wrap">
          <NovaTextarea
            v-model="activity.value"
            :placeholder="activity.placeholder"
            :rows="6"
            :min-length-met="activity.count"
            :max-length="700"
            :error-msg="activity.errorMsg"
            @on-change="activity.onChangeValue"
          />
        </div>
      </section>

      <section class="section">
        <div class="section-title-wrap">
          <h4 class="section-title">
            {{ t('applyCreator.conditionsContent.sections.info.title') }}
          </h4>
        </div>

        <ClientOnly>
          <div class="form-field-wrap">
            <div v-if="applyType === 'CUAR'">
              <div class="dynamic-form-wrap">
                <div class="section-sub-title">
                  {{
                    t(
                      'applyCreator.conditionsContent.sections.info.representative',
                      {
                        count: representative.count,
                      }
                    )
                  }}
                  <div class="select-area">
                    <Tippy
                      ref="newRepresentativeRef"
                      :interactive="true"
                      :trigger="'click'"
                      :theme="'popover'"
                      :placement="'bottom-end'"
                    >
                      <NovaButtonIcon
                        :icon="{
                          type: 'outline',
                          name: 'add',
                        }"
                        :theme="'primary-blue-light'"
                        :size="32"
                        :class="[
                          'contents-type-selector',
                          newRepresentative.type,
                        ]"
                      />
                      <template #content>
                        <div class="contents-type-selector-panel">
                          <button
                            type="button"
                            class="change-content-type link"
                            @click="
                              () =>
                                newRepresentative.onClickContentType(
                                  ApplyCreatorAttachContentType.LINK
                                )
                            "
                          >
                            <NovaIcon
                              :icon="{ type: 'outline', name: 'link' }"
                            />
                            <span class="label">
                              {{
                                t(
                                  'applyCreator.conditionsContent.sections.info.contentsLink'
                                )
                              }}
                            </span>
                          </button>
                          <button
                            type="button"
                            class="change-content-type file"
                            @click="
                              () =>
                                newRepresentative.onClickContentType(
                                  ApplyCreatorAttachContentType.FILE
                                )
                            "
                          >
                            <NovaIcon
                              :icon="{ type: 'outline', name: 'file' }"
                            />
                            <span class="label">
                              {{
                                t(
                                  'applyCreator.conditionsContent.sections.info.contentsFile'
                                )
                              }}
                            </span>
                          </button>
                        </div>
                      </template>
                    </Tippy>
                  </div>
                </div>

                <div class="dynamic-form-fields">
                  <!-- 엠프티 필드 -->
                  <div
                    v-if="
                      !newRepresentative.isShowAddField &&
                      !representative.value.length
                    "
                    class="empty-field"
                  >
                    <span
                      v-dompurify-html="
                        t(
                          'applyCreator.conditionsContent.sections.info.contentsEmpty'
                        )
                      "
                      class="message"
                    />
                  </div>

                  <!-- 추가된 필드 -->
                  <div
                    v-for="(item, index) in representative.value"
                    :key="index"
                    class="preview-wrap"
                  >
                    <NovaIcon
                      :icon="{
                        type: 'outline',
                        name: getContentTypeIcon(item.type),
                      }"
                      class="type-icon"
                    />
                    <p class="preview">
                      <span>{{ item.value.url }}</span>
                    </p>

                    <NovaButtonText
                      :label="newRepresentative.deleteLabel"
                      :theme="'primary-red-light'"
                      :size="38"
                      :loading="newRepresentative.addLoading"
                      class="action"
                      @click="() => handleOnClickRemoveRepresentative(index)"
                    />
                  </div>

                  <!-- 등록 필드 -->
                  <div
                    v-if="
                      newRepresentative.isShowAddField &&
                      representative.value.length < representative.maxCount
                    "
                    class="create-field"
                  >
                    <NovaInput
                      v-model="newRepresentative.value.url"
                      :placeholder="newRepresentative.placeholder"
                      :readonly="
                        newRepresentative.type ===
                        ApplyCreatorAttachContentType.FILE
                      "
                      :is-clear="
                        newRepresentative.type ===
                        ApplyCreatorAttachContentType.LINK
                      "
                      @click="newRepresentative.onRepresentativeFile"
                    />

                    <NovaButtonText
                      :label="newRepresentative.addLabel"
                      :theme="'primary-blue-light'"
                      :size="38"
                      :loading="newRepresentative.addLoading"
                      class="action"
                      @click="newRepresentative.onRepresentativeAdd"
                    />
                  </div>
                </div>
              </div>

              <p v-if="representative.errorMsg" class="error-message">
                {{ representative.errorMsg }}
              </p>

              <NovaDescriptions
                :descriptions="representative.descriptions"
                class="mg-t-3u"
              />
            </div>

            <div>
              <div
                :class="[
                  'section-sub-title mg-b-2u',
                  { 'mg-t-4u': applyType === 'CUAR' },
                ]"
              >
                {{ t('applyCreator.conditionsContent.sections.info.sns') }}
                {{
                  applyType === 'CUAR'
                    ? `(${t('optional')})`
                    : t(
                        'applyCreator.conditionsContent.sections.info.snsSubTitle'
                      )
                }}
              </div>

              <div class="sns-field">
                <NovaInput
                  v-model="sns.value"
                  :placeholder="sns.placeholder"
                  @on-change="sns.onChangeValue"
                />
                <NovaCheckbox
                  v-if="applyType === 'CUFW'"
                  :label="
                    t(
                      'applyCreator.conditionsContent.sections.info.verifiedSnsAccount'
                    )
                  "
                  :is-check="verifiedSnsAccount.value"
                  :is-error="verifiedSnsAccount.isError"
                  @click="verifiedSnsAccount.onChangeValue"
                />
              </div>

              <p v-if="sns.errorMsg" class="error-message">
                {{ sns.errorMsg }}
              </p>
            </div>
          </div>
        </ClientOnly>
      </section>

      <section class="section">
        <div class="section-title-wrap">
          <h4 class="section-title">
            {{
              t('applyCreator.conditionsContent.sections.creatorCategory.title')
            }}
          </h4>
        </div>

        <div class="form-field-wrap">
          <NovaDropdown
            :placeholder="creatorCategory.placeholder"
            :menus="creatorCategory.categories"
            :active-value="creatorCategory.value"
            @update="creatorCategory.onChangeValue"
          />

          <p v-if="creatorCategory.errorMsg" class="error-message">
            {{ creatorCategory.errorMsg }}
          </p>
        </div>
      </section>

      <ClientOnly>
        <section class="section">
          <div class="section-title-wrap">
            <h4 class="section-title">
              {{
                t(
                  'applyCreator.conditionsContent.sections.creatorHomeLink.title'
                )
              }}
            </h4>
          </div>

          <div class="form-field-wrap">
            <div class="preview-wrap mg-b-1u">
              <p class="preview">
                {{ `${creatorHomeLink.homeUrl}${creatorHomeLink.value}` }}
              </p>
            </div>

            <NovaInput
              v-if="!creatorHomeLink.isRegistered"
              v-model="creatorHomeLink.value"
              :prefix="creatorHomeLink.prefix"
              @on-change="handleOnChangeCreatorHomeLink"
              @on-clear="handleOnChangeCreatorHomeLink"
            >
              <template #actions>
                <NovaButtonText
                  :label="
                    t(
                      `applyCreator.conditionsContent.sections.creatorHomeLink.${
                        creatorHomeLink.isCheckDuplicate
                          ? 'checkedDuplicate'
                          : 'checkDuplicate'
                      }`
                    )
                  "
                  :theme="
                    creatorHomeLink.isCheckDuplicate
                      ? 'primary-red-light'
                      : 'primary-blue-light'
                  "
                  :loading="creatorHomeLink.loading"
                  @click="() => creatorHomeLink.checkDuplicate()"
                />
              </template>
            </NovaInput>

            <p v-if="creatorHomeLink.errorMsg" class="error-message">
              {{ creatorHomeLink.errorMsg }}
            </p>
          </div>
        </section>
      </ClientOnly>

      <section class="section">
        <div class="section-title-wrap">
          <h4 class="section-title">
            {{
              t('applyCreator.conditionsContent.sections.howToKnow.title', {
                appName: config.public['APP_NAME'],
              })
            }}
            {{ `(${t('optional')})` }}
          </h4>
        </div>

        <NovaInput
          v-model="howToKnow.value"
          :placeholder="howToKnow.placeholder"
          :max-length="howToKnow.maxLength"
        />
      </section>

      <div class="request-review mg-t-6u">
        <NovaButtonText
          :label="reqReview.label"
          :theme="'primary-blue'"
          :full-width="false"
          :width="200"
          :loading="reqReview.loading"
          @click="handleOnClickReqReview"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: $color-primary-red;
  content: '!';
  line-height: 1;
  font-size: 14px;
  font-weight: 700;
  color: $color-white;
}

.page-apply-creator-condition-contents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;

  :deep(.mobile-header) {
    width: 100%;
    max-width: 632px + 24px + 24px;

    @include mobile {
      max-width: 100%;
    }
  }

  > .inner {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 632px + 24px + 24px;
    padding: 15px 24px 48px;
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 42px;

    .section-title-wrap {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 12px;

      .section-title {
        @include text-style($text-heading2-bold);
        color: $color-text-1;

        > small {
          @include text-style($text-body-14-medium);
          color: $color-text-3;
        }
      }

      .extra {
        display: flex;
        align-items: center;
        gap: 6px;
      }
    }

    .section-sub-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      @include text-style($text-display-bold);
      color: $color-text-2;
    }

    .form-field-wrap {
      display: flex;
      flex-direction: column;

      .error-message {
        display: flex;
        align-items: center;
        gap: 6px;
        @include text-style($text-body-12-regular);
        color: $color-primary-red;
        margin-top: 12px;

        &:before {
          @include error-icon;
        }
      }
    }

    .dynamic-form-wrap {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .select-area {
        .contents-type-selector-panel {
          display: flex;
          flex-direction: column;
          padding: 10px 16px;
          background-color: $color-bg-3;
          box-shadow: 0 4px 20px hex-to-rgba($color-black, 0.08);
          border-radius: 8px;

          .change-content-type {
            display: flex;
            align-items: center;
            gap: 4px;

            @media (hover: hover) {
              &:hover {
                color: $color-primary-blue;
              }
            }

            .link {
              font-size: 30px !important;
            }

            :deep(.icon) {
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 30px;
              height: 30px;
            }

            .label {
              text-align: left;
              @include ellipsis(1);
            }
          }
        }
      }

      .dynamic-form-fields {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .empty-field {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          width: 100%;
          height: 160px;
          padding: 12px;
          border-radius: 8px;
          background-color: $color-bg-custom-5;
          color: $color-text-3;
          text-align: center;

          .message {
            @include text-style($text-body-14-regular);
          }
        }

        .create-field {
          display: flex;
          align-items: center;
          gap: 4px;

          .contents-type-selector {
            color: $color-primary-blue;
            :deep(.icon) {
              font-size: 20px !important;
            }
          }
        }

        .action {
          flex-shrink: 0;
          width: 80px;
        }
      }
    }

    .preview-wrap {
      display: flex;
      align-items: center;
      gap: 4px;

      .preview {
        flex-grow: 1;
        display: flex;
        align-items: center;
        height: 40px;
        padding: 0 15px;
        font-weight: 400;
        font-size: 14px;
        line-height: 18px;
        letter-spacing: -0.55px;
        border-radius: 8px;
        background-color: #f7f7f7;

        > span {
          @include ellipsis(1);
        }
      }

      .type-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        font-size: 32px !important;
        color: $color-text-3;

        &.file {
          font-size: 20px !important;
        }
      }
    }

    .sns-field {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;

      .checkbox-wrap {
        display: flex;
        align-items: center;
        gap: 4px;

        .error-icon {
          &:before {
            @include error-icon;
          }
        }
      }
    }

    // 신청 버튼
    &.apply {
      margin-top: 65px;
      align-items: center;
    }

    > .label {
      @include text-style($text-body-14-bold);
      line-height: 16px;
      color: $color-text-1;
    }
  }

  .request-review {
    display: flex;
    justify-content: center;
  }
}
</style>
